import { Typography } from '@euler/app/components/typography';
import { ProductItemCell } from '@euler/app/flows/products/components/ProductItemCell';
import type { ProductNavParams } from '@euler/app/flows/products/screens';
import { useSiteInspectionNavigatorContext } from '@euler/app/flows/task/pages/inspection/site-inspection/SiteInspectionContext';
import { deriveManifestItemAdopter } from '@euler/functions/product/deriveManifestItemAdopter';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import { arr2groupmap } from '@euler/utils/array';
import { useLocalObservable } from '@euler/utils/hooks';
import { BottomSheetSectionList } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useMemo } from 'react';
import { ListRenderItem, SectionList, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Observable } from 'rxjs';

const keyExtractor = (item: TaskProductManifestItem) => String(item.id);

type Section = {
  subject: string;
  index: number;
  data: TaskProductManifestItem[];
};

export const ProductManifestPage = wrapNavigatorScreen(
  ({
    items$,
    onRemove,
    onEditCloser,
  }: {
    items$: Observable<TaskProductManifestItem[]>;
    onRemove?: (item: TaskProductManifestItem) => void;
    onEditCloser?: (item: TaskProductManifestItem) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const navigation = useNavigation<StackNavigationProp<ProductNavParams>>();
    const siteInspectionNavContext = useSiteInspectionNavigatorContext();
    const bottomComponent$ = siteInspectionNavContext?.bottomComponent$;

    const items = useLocalObservable(items$, []);

    const sections = useMemo<Section[]>(() => {
      const groups = arr2groupmap(items, x => x.subject);
      return [...groups.entries()].map<Section>((group, index) => ({
        subject: group[0],
        data: group[1],
        index,
      }));
    }, [items]);

    const onItemPress = useCallback(
      (item: TaskProductManifestItem) => {
        navigation.navigate('_productDetail', { id: item.product.id });
      },
      [navigation],
    );

    const renderItem: ListRenderItem<TaskProductManifestItem> = useCallback(
      ({ item }) => {
        const adopter = deriveManifestItemAdopter(item);
        return (
          <ProductItemCell
            item={item}
            name={item.product.name}
            title={item.product.title}
            coverUrl={item.product.coverUrl}
            showBadge={true}
            qty={item.qty}
            removable={
              !item.jobRefs?.length &&
              (item.confirmations == null ||
                !Object.values(item.confirmations).at(0)?.adopted)
            }
            adopted={
              item.jobRefs?.length
                ? true
                : item.confirmations
                ? Object.values(item.confirmations).at(0)?.adopted
                : undefined
            }
            author={item.author}
            adopter={adopter}
            closer={item.closer ?? undefined}
            onEditCloser={onEditCloser}
            onPress={onItemPress}
            onRemove={onRemove}
            css={`
              margin: ${rpx(1)}px 0;
            `}
          />
        );
      },
      [onEditCloser, onItemPress, onRemove, rpx],
    );

    const ListComponent =
      siteInspectionNavContext?.navigatorContainerType === 'bottomsheet'
        ? BottomSheetSectionList
        : SectionList;

    useEffect(() => {
      if (bottomComponent$ == null) return;
      const bottomComponent = bottomComponent$.value;
      bottomComponent$.next(undefined);
      return () => bottomComponent$.next(bottomComponent);
    }, [bottomComponent$]);

    __DEV__ && console.log(JSON.stringify(items, null, 2));

    return (
      <ListComponent
        sections={sections}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        stickySectionHeadersEnabled={false}
        initialNumToRender={50}
        contentContainerStyle={{
          backgroundColor: '#fafafa',
          padding: rpx(15),
          paddingBottom: rpx(68) + bottom,
          flexGrow: 1,
        }}
        renderSectionHeader={({ section }) => (
          <View
            css={`
              background-color: #fafafa;
              margin-top: ${rpx(section.index === 0 ? 4 : 12)}px;
              margin-bottom: ${rpx(4)}px;
            `}
          >
            <Typography.Label medium size={rpx(14)} lineHeight={rpx(19)}>
              {section.subject}
            </Typography.Label>
          </View>
        )}
      />
    );
  },
  {
    title: '产品清单',
  },
);
